import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Text,
  Flex,
  Center,
  IconButton,
  Icon,
  CircularProgress,
  Box,
  Image,
  Card,
  CardBody,
  Skeleton,
  Badge,
  Button,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  FaBookOpen,
  FaArrowLeft,
} from "react-icons/fa";
import { Auth } from "aws-amplify";
import axios from "axios";
import AWS from "aws-sdk";
import { GuideDrawer } from "../Drawer/GuideDrawer";
import { PropertySectionDrawer } from "../Drawer/PropertySectionDrawer";
import { HubNavigation } from "../Components/HubNavigation";
import {
  buildPropertyGuideItems,
  ALL_CATALOG_ID,
  DIRECTIONS_ITEM_ID,
  PROPERTY_CATALOG_ID,
  PROPERTY_CATALOG_NAME,
} from "../Logic/propertyGuideItems";
import { useNavigate, useLocation } from "react-router-dom";
import { propertyLinkPath } from "../../../Helper/propertyLink";
import { guestResumePath } from "../../../Helper/guestStay";

// Configure AWS for S3 image fetching
AWS.config.update({
  region: "us-west-2",
  credentials: new AWS.Credentials(
    process.env.REACT_APP_PUBLIC_KEY,
    process.env.REACT_APP_SECRET_KEY
  ),
});

// GuideCard component
const GuideCard = React.memo(({ guide, imageUrl, isImageLoaded, hasImageError, onImageLoad, onImageError, onGuideClick }) => {
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <Card
      bg="rgba(255, 255, 255, 0.05)"
      border="1px solid"
      borderColor="rgba(255, 255, 255, 0.1)"
      borderRadius="12px"
      overflow="hidden"
      transition="all 0.3s ease"
      mb={4}
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
        borderColor: 'rgba(129, 126, 255, 0.3)',
      }}
      cursor="pointer"
      onClick={() => onGuideClick(guide)}
    >
      {imageUrl && !hasImageError && (
        <Box position="relative" overflow="hidden">
          {!isImageLoaded && (
            <Skeleton h="120px" />
          )}
          <Image
            src={imageUrl}
            alt={guide.title}
            w="full"
            h="auto"
            maxH="300px"
            objectFit="cover"
            opacity={isImageLoaded ? 1 : 0}
            transition="opacity 0.3s ease"
            onLoad={() => onImageLoad(guide.id)}
            onError={() => onImageError(guide.id)}
          />
        </Box>
      )}
      
      <CardBody p={3} pt={4} flex="1" display="flex" flexDirection="column">
        <Flex direction="column" gap={2} flex="1">
          <Text fontSize="14px" fontWeight="700" color="white" lineHeight="1.3" noOfLines={2}>
            {guide.title}
          </Text>
          
          {/* Property sections carry a pre-flattened `excerpt` because their
              body is rich HTML — printing `description` would show raw tags. */}
          <Text fontSize="12px" color="gray.300" lineHeight="1.4" noOfLines={4} flex="1">
            {truncateText(guide.excerpt ?? guide.description, 150)}
          </Text>
          
          <Flex justify="space-between" align="center" mt="auto">
            <Badge colorScheme="purple" variant="subtle" fontSize="9px" px={2} py={1}>
              {guide.catalog?.name || "Guide"}
            </Badge>
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
});

export const GuidesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { room, imageUrl: heroImageUrl, initialCatalog } = location.state || {};

  // Property guide sections live on the room record, so they need no request
  // and are always available instantly.
  const propertyItems = useMemo(
    () => buildPropertyGuideItems(room, heroImageUrl),
    [room, heroImageUrl]
  );

  // Directions live in the floating bar, not in this grid, so they're
  // filtered out of the tabbed content.
  const gridPropertyItems = useMemo(
    () => propertyItems.filter((item) => item.id !== DIRECTIONS_ITEM_ID),
    [propertyItems]
  );

  // Entry points can deep-link straight to a tab — the main page's "Info"
  // button lands on "The Property" so the info people were missing is the
  // first thing on screen. Fall back to "All" when this room has no tabbed
  // property content (an address alone doesn't count: directions aren't in
  // the grid), otherwise the deep link would select a tab that isn't
  // rendered.
  const startingCatalog =
    initialCatalog === PROPERTY_CATALOG_ID && gridPropertyItems.length === 0
      ? ALL_CATALOG_ID
      : initialCatalog || ALL_CATALOG_ID;

  const [guides, setGuides] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageLoadStates, setImageLoadStates] = useState({});
  const [imageUrls, setImageUrls] = useState({});
  const [selectedCatalog, setSelectedCatalog] = useState(startingCatalog);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [sectionOpen, setSectionOpen] = useState(false);

  const observerRef = useRef();
  const loadingRef = useRef();

  // Redirect if no room data
  useEffect(() => {
    if (!room) {
      const propertyPath = propertyLinkPath();
      if (propertyPath) {
        navigate(propertyPath, { replace: true });
        return;
      }
      navigate(guestResumePath(), { replace: true });
    }
  }, [room, navigate]);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Two detail renderers, picked by content kind: property sections are rich
  // HTML from the dashboard editor, API guides are plain text that gets
  // URL-autolinked. See Logic/propertyGuideItems.js.
  const handleItemClick = useCallback((item) => {
    if (item?.kind === "property") {
      setSelectedSection(item);
      setSectionOpen(true);
      return;
    }
    setSelectedGuide(item);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedGuide(null);
  }, []);

  const handleSectionClose = useCallback(() => {
    setSectionOpen(false);
    setSelectedSection(null);
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Memoize the fetchImageUrls function to prevent infinite loops
  const fetchImageUrls = useCallback(async (guidesData) => {
    const newImageUrls = {};
    const cloudfrontBaseUrl = "https://dl6ri8ttf37m3.cloudfront.net";

    for (const guide of guidesData) {
        // Guides can have no `images` key at all, or an empty array — text-only
        // guides are normal, and the cards render fine without an image.
        const imageKey = guide?.images?.[0];
        if (typeof imageKey !== "string" || !imageKey) continue;

        if (imageKey.startsWith("http")) {
          newImageUrls[guide.id] = imageKey;
          continue;
        }

        const optimizedUrl = `${cloudfrontBaseUrl}/public/${encodeURIComponent(imageKey)}?width=600&format=webp`;
        newImageUrls[guide.id] = optimizedUrl;
    }

    setImageUrls(prev => ({ ...prev, ...newImageUrls }));
  }, []);

  // Fetch catalogs
  const fetchCatalogs = useCallback(async () => {
    if (!room?.id) return;
    
    setCatalogsLoading(true);
    
    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();

      const response = await axios.get(`https://api.linkbase.ca/guides/catalogs/by-room/${room.id}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
        }
      });

      const catalogsData = response.data?.data?.catalogs || [];
      setCatalogs(catalogsData);
    } catch (err) {
      console.error('Error fetching catalogs:', err);
    } finally {
      setCatalogsLoading(false);
    }
  }, [room?.id]);

  // Fetch guides by catalog
  const fetchGuidesByCatalog = useCallback(async (catalogId, pageToken = null, append = false) => {
    if (!room?.id) return;
    
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();

      let url = `https://api.linkbase.ca/guides/by-catalog/${catalogId}?roomId=${room.id}`;
      if (pageToken) {
        url += `&nextPageToken=${encodeURIComponent(pageToken)}`;
      }

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
        }
      });

      const guidesWithCatalogs = response.data?.data?.guidesWithCatalogs || [];
      const guidesData = guidesWithCatalogs.map(item => ({
        ...item.guide,
        catalog: item.catalog
      }));
      
      if (append) {
        setGuides(prev => {
          const newGuides = [...prev, ...guidesData];
          console.log(`📚 Loaded more guides: ${guidesData.length} new guides, total: ${newGuides.length}`);
          return newGuides;
        });
      } else {
        console.log(`📚 Initial load: ${guidesData.length} guides`);
        setGuides(guidesData);
      }

      
      setNextPageToken(response.data?.data?.nextPageToken || null);
      setHasMore(!!response.data?.data?.nextPageToken);
      
      // Image URLs are cosmetic: keep any failure here out of the fetch's
      // error path so a loaded list is never reported as a failed load.
      try {
        await fetchImageUrls(guidesData);
      } catch (imageErr) {
        console.error('Error preparing guide image URLs:', imageErr);
      }
    } catch (err) {
      console.error('Error fetching guides:', err);
      setError('Failed to load guides');
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [room?.id, fetchImageUrls]);

  // Handle catalog selection
  const handleCatalogSelect = useCallback((catalogId) => {
    setSelectedCatalog(catalogId);
    setNextPageToken(null);
    setError(null);
    // Scroll to top when category changes
    window.scrollTo(0, 0);

    // Drop the previous tab's results so they don't linger under the new tab
    // while its request is in flight.
    setGuides([]);

    if (catalogId === PROPERTY_CATALOG_ID) {
      // Property sections are already in hand — no fetch, no paging.
      setHasMore(false);
      return;
    }

    setHasMore(true);
    fetchGuidesByCatalog(catalogId);
  }, [fetchGuidesByCatalog]);

  // Load more guides
  const loadMoreGuides = useCallback(() => {
    if (hasMore && !loadingMore && nextPageToken) {
      fetchGuidesByCatalog(selectedCatalog, nextPageToken, true);
    }
  }, [hasMore, loadingMore, nextPageToken, selectedCatalog, fetchGuidesByCatalog]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreGuides();
        }
      },
      { threshold: 0.1 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loadMoreGuides]);

  useEffect(() => {
    setImageLoadStates({});
    setImageUrls({});
    setSelectedCatalog(startingCatalog);
    setNextPageToken(null);
    fetchCatalogs();

    if (startingCatalog === PROPERTY_CATALOG_ID) {
      setGuides([]);
      setHasMore(false);
      return;
    }

    setHasMore(true);
    fetchGuidesByCatalog(startingCatalog);
  }, [fetchCatalogs, fetchGuidesByCatalog, startingCatalog]);

  const handleImageLoad = useCallback((guideId) => {
    setImageLoadStates(prev => ({
      ...prev,
      [guideId]: true
    }));
  }, []);

  const handleImageError = useCallback((guideId) => {
    setImageLoadStates(prev => ({
      ...prev,
      [guideId]: 'error'
    }));
  }, []);

  // Tab strip: "All" first, then the property sections, then the host's
  // catalogs. "All" is explicit now that the hub mixes sources — previously
  // the default selection matched no visible tab.
  const tabs = useMemo(() => {
    const list = [{ id: ALL_CATALOG_ID, name: "All" }];
    if (gridPropertyItems.length > 0) {
      list.push({ id: PROPERTY_CATALOG_ID, name: PROPERTY_CATALOG_NAME });
    }
    catalogs.forEach((catalog) => {
      // The API has returned an "all" catalog historically; don't double it up.
      if (catalog?.id && catalog.id !== ALL_CATALOG_ID) {
        list.push(catalog);
      }
    });
    return list;
  }, [catalogs, gridPropertyItems]);

  // Property sections lead the list: they're the content guests were missing
  // behind the old "Explore the Property" button.
  const visibleItems = useMemo(() => {
    const includeProperty =
      selectedCatalog === ALL_CATALOG_ID ||
      selectedCatalog === PROPERTY_CATALOG_ID;
    const includeGuides = selectedCatalog !== PROPERTY_CATALOG_ID;

    return [
      ...(includeProperty ? gridPropertyItems : []),
      ...(includeGuides ? guides : []),
    ];
  }, [selectedCatalog, gridPropertyItems, guides]);

  // Only block the whole view when there is genuinely nothing to show yet;
  // on "All" the property cards render while guides are still loading.
  const showInitialSpinner = loading && visibleItems.length === 0;
  const showPagingSentinel = hasMore && selectedCatalog !== PROPERTY_CATALOG_ID;


  // Pinterest-style masonry layout — 2 columns on phones, more on desktop
  const columnCount = useBreakpointValue({ base: 2, md: 3, xl: 4 }) || 2;

  const masonryColumns = useMemo(() => {
    const columns = Array.from({ length: columnCount }, () => []);
    const columnHeights = Array.from({ length: columnCount }, () => 0);

    visibleItems.forEach((item) => {
      const minHeightIndex = columnHeights.indexOf(Math.min(...columnHeights));
      columns[minHeightIndex].push(item);
      const estimatedHeight = 300 + 16;
      columnHeights[minHeightIndex] += estimatedHeight;
    });

    return columns;
  }, [visibleItems, columnCount]);

  // Rest of the component logic will be added in the next edit
  return (
    <Box bg="#1A202C" color="white" minH="100vh" display="flex" flexDirection="column">
      {/* Sticky Header and Categories */}
      <Box position="sticky" top={0} zIndex={10} bg="#1A202C">
        {/* Header */}
        <Box px={{ base: 3, md: 8 }} pt={4} pb={3} maxW="1100px" mx="auto" w="100%">
          <Flex alignItems="center">
            <IconButton
              icon={<Icon as={FaArrowLeft} />}
              variant="ghost"
              colorScheme="blue"
              bg="#2c3b4b"
              mr={2}
              onClick={handleBack}
              aria-label="Back"
            />
            <Flex align="center" gap={2}>
              <Text fontWeight="bold" fontSize={{ base: "14px", md: "18px" }}>
                Info &amp; Guides
              </Text>
              <FaBookOpen />
            </Flex>
          </Flex>
        </Box>

        {/* Catalog Tabs */}
        <Box borderBottom="1px solid" borderColor="rgba(255, 255, 255, 0.1)">
          {/* "All" and the property tab exist without any request, so only
              fall back to skeletons when there is nothing real to show. */}
          {catalogsLoading && tabs.length <= 1 ? (
            <Flex gap={2} overflowX="auto" pb={2} px={{ base: 2, md: 8 }} maxW="1100px" mx="auto">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} h="32px" w="80px" borderRadius="16px" />
              ))}
            </Flex>
          ) : (
            <Flex gap={2} overflowX="auto" pb={2} px={{ base: 2, md: 8 }} maxW="1100px" mx="auto" sx={{
              '&::-webkit-scrollbar': {
                height: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '2px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(129, 126, 255, 0.5)',
                borderRadius: '2px',
              },
            }}>
              {tabs.map((catalog) => (
                <Button
                  key={catalog.id}
                  size="sm"
                  variant={selectedCatalog === catalog.id ? "solid" : "outline"}
                  bg={selectedCatalog === catalog.id ? "#817EFF" : "transparent"}
                  color={selectedCatalog === catalog.id ? "white" : "#817EFF"}
                  borderColor="#817EFF"
                  borderRadius="16px"
                  px={4}
                  py={2}
                  fontSize="12px"
                  fontWeight="600"
                  whiteSpace="nowrap"
                  flexShrink={0}
                  onClick={() => handleCatalogSelect(catalog.id)}
                  _hover={{
                    bg: selectedCatalog === catalog.id ? "#615edc" : "rgba(129, 126, 255, 0.1)",
                  }}
                >
                  {catalog.name}
                </Button>
              ))}
            </Flex>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box flex="1" overflowY="auto" px={{ base: 4, md: 8 }} pb={6} maxW="1100px" mx="auto" w="100%">
        {showInitialSpinner ? (
          <Center h="200px">
            <CircularProgress
              isIndeterminate
              color="#504CFF"
              size="30px"
              trackColor="transparent"
            />
          </Center>
        ) : error && visibleItems.length === 0 ? (
          <Center h="200px">
            <Text fontSize="14px" color="red.400" fontWeight={600}>
              {error}
            </Text>
          </Center>
        ) : visibleItems.length === 0 ? (
          <Center h="200px">
            <Text fontSize="14px" color="gray.400" fontWeight={600}>
              No guides available in this category.
            </Text>
          </Center>
        ) : (
          <Box maxW="100%" overflow="hidden" pt={'10px'}> 
            {/* Pinterest-style Masonry Layout */}
            <Flex gap={{ base: 4, md: 5 }} maxW="100%">
              {masonryColumns.map((column, columnIndex) => (
                <Box key={columnIndex} flex="1" minW="0">
                  {column.map((item) => (
                    <GuideCard 
                      key={item.id} 
                      guide={item}
                      // Property items already hold a usable URL; API guides
                      // get theirs from the resolved CloudFront map.
                      imageUrl={
                        item.kind === "property"
                          ? item.images?.[0]
                          : imageUrls[item.id]
                      }
                      isImageLoaded={imageLoadStates[item.id]}
                      hasImageError={imageLoadStates[item.id] === 'error'}
                      onImageLoad={handleImageLoad}
                      onImageError={handleImageError}
                      onGuideClick={handleItemClick}
                    />
                  ))}
                </Box>
              ))}
            </Flex>
            
            {/* Loading indicator for infinite scroll */}
            {showPagingSentinel && (
              <Box ref={loadingRef} py={4} textAlign="center">
                {loadingMore ? (
                  <CircularProgress
                    isIndeterminate
                    color="#504CFF"
                    size="24px"
                    trackColor="transparent"
                  />
                ) : (
                  <Text fontSize="12px" color="gray.400">
                    Scroll for more guides...
                  </Text>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Same floating Directions / Map bar as the main page, minus "Info" —
          this is the Info page. */}
      <HubNavigation room={room} />

      <GuideDrawer
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        guide={selectedGuide}
      />

      <PropertySectionDrawer
        isOpen={sectionOpen}
        onClose={handleSectionClose}
        section={selectedSection}
      />
    </Box>
  );
}; 